import { useState, useCallback, useRef } from 'react';
import { X, Upload, Download, FileSpreadsheet, Users, Scissors, AlertCircle, CheckCircle } from 'lucide-react';
import { useSalonId } from '../../hooks/useSalonId';
import { clientApi } from '../../api/clientApi';
import { serviceApi } from '../../api/serviceApi';
import { employeeApi } from '../../api/employeeApi';

type ImportType = 'services' | 'employees' | 'clients';

interface ImportDataModalProps {
    onClose: () => void;
    onImportSuccess?: () => void;
}

interface ImportResult {
    success: number;
    failed: number;
    errors: string[];
}

const ImportDataModal = ({ onClose, onImportSuccess }: ImportDataModalProps) => {
    const salonId = useSalonId();
    const [activeTab, setActiveTab] = useState<ImportType>('services');
    const [dragActive, setDragActive] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string[][]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [hasSuccessfulImport, setHasSuccessfulImport] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClose = () => {
        onClose();
        if (hasSuccessfulImport && onImportSuccess) {
            onImportSuccess();
        }
    };

    const tabs: { id: ImportType; label: string; icon: React.ReactNode }[] = [
        { id: 'services', label: 'Servicios', icon: <Scissors className="w-4 h-4" /> },
        { id: 'employees', label: 'Empleados', icon: <Users className="w-4 h-4" /> },
        { id: 'clients', label: 'Clientes', icon: <Users className="w-4 h-4" /> },
    ];

    const getTemplateHeaders = (type: ImportType): string[] => {
        switch (type) {
            case 'services':
                return ['nombre', 'duracion_minutos', 'precio'];
            case 'employees':
                return ['nombre_completo', 'email', 'telefono', 'rol'];
            case 'clients':
                return ['nombre_completo', 'telefono', 'email', 'notas'];
            default:
                return [];
        }
    };

    const getTemplateExample = (type: ImportType): string[][] => {
        switch (type) {
            case 'services':
                return [
                    ['Corte de cabello', '45', '35000'],
                    ['Tinte completo', '120', '150000'],
                    ['Manicure', '30', '25000'],
                ];
            case 'employees':
                return [
                    ['María García', 'maria@email.com', '3001234567', 'STYLIST'],
                    ['Juan Pérez', 'juan@email.com', '3009876543', 'RECEPTIONIST'],
                ];
            case 'clients':
                return [
                    ['Ana Martínez', '3001112233', 'ana@email.com', 'Cliente VIP'],
                    ['Carlos López', '3004445566', '', 'Prefiere citas en la mañana'],
                ];
            default:
                return [];
        }
    };

    const downloadTemplate = () => {
        const headers = getTemplateHeaders(activeTab);
        const examples = getTemplateExample(activeTab);

        const csvContent = [
            headers.join(','),
            ...examples.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `plantilla_${activeTab}.csv`;
        link.click();
    };

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const parseCSV = (text: string): string[][] => {
        const lines = text.split('\n').filter(line => line.trim());
        return lines.map(line => {
            // Simple CSV parsing (doesn't handle all edge cases)
            return line.split(',').map(cell => cell.trim().replace(/^["']|["']$/g, ''));
        });
    };

    const [parsedData, setParsedData] = useState<string[][]>([]);

    const handleFile = (uploadedFile: File) => {
        setFile(uploadedFile);
        setResult(null);

        const readFile = (encoding: string) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result as string;

                // If we detect the replacement character  in UTF-8, it's likely a legacy encoding
                if (encoding === 'UTF-8' && text.includes('\ufffd')) {
                    console.log('Detected encoding issue, retrying with ISO-8859-1');
                    readFile('ISO-8859-1');
                    return;
                }

                const parsed = parseCSV(text);
                setParsedData(parsed);
                setPreview(parsed.slice(0, 6)); // Only show first 5 rows + header for preview
            };
            reader.readAsText(uploadedFile, encoding);
        };

        readFile('UTF-8');
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleImport = async () => {
        if (!file || !salonId) return;

        setIsUploading(true);
        setResult(null);

        try {
            const rows = parsedData.slice(1).filter(r => r.some(c => c)); // Exclude header and empty rows
            let response;

            if (activeTab === 'clients') {
                const clients = rows.map(r => ({
                    fullName: r[0],
                    phone: r[1],
                    email: r[2],
                    notes: r[3]
                }));
                response = await clientApi.bulkImport({ salonId, clients });
            } else if (activeTab === 'services') {
                const services = rows.map(r => ({
                    name: r[0],
                    durationMinutes: parseInt(r[1]) || 30,
                    price: parseFloat(r[2]) || 0
                }));
                response = await serviceApi.bulkImport({ salonId, services });
            } else if (activeTab === 'employees') {
                const employees = rows.map(r => ({
                    fullName: r[0],
                    email: r[1],
                    phone: r[2],
                    role: r[3] || 'STYLIST'
                }));
                response = await employeeApi.bulkImport({ salonId, employees });
            }

            if (response) {
                setResult({
                    success: response.successCount,
                    failed: response.failedCount,
                    errors: response.errors
                });
                if (response.successCount > 0) {
                    setHasSuccessfulImport(true);
                }
            }
        } catch (error) {
            console.error('Import failed', error);
            setResult({
                success: 0,
                failed: 0,
                errors: ['Error de conexión con el servidor']
            });
        } finally {
            setIsUploading(false);
        }
    };

    const resetForm = () => {
        setFile(null);
        setPreview([]);
        setParsedData([]);
        setResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <Upload className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Importar Datos</h2>
                            <p className="text-sm text-gray-500">Carga masiva de servicios, empleados o clientes</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-100">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); resetForm(); }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === tab.id
                                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {/* Download Template */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                                <div>
                                    <p className="text-sm font-medium text-blue-800">Descarga la plantilla</p>
                                    <p className="text-xs text-blue-600">Usa nuestra plantilla CSV para evitar errores</p>
                                </div>
                            </div>
                            <button
                                onClick={downloadTemplate}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Descargar
                            </button>
                        </div>
                    </div>

                    {/* Drop Zone */}
                    {!result && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            className={`
                                border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                                ${dragActive ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                                ${file ? 'border-emerald-500 bg-emerald-50' : ''}
                            `}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileChange}
                                className="hidden"
                            />

                            {file ? (
                                <div className="flex flex-col items-center gap-2">
                                    <CheckCircle className="w-10 h-10 text-emerald-500" />
                                    <p className="text-sm font-medium text-emerald-700">{file.name}</p>
                                    <p className="text-xs text-emerald-600">{(file.size / 1024).toFixed(1)} KB</p>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); resetForm(); }}
                                        className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                                    >
                                        Cambiar archivo
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                    <p className="text-sm font-medium text-gray-700 mb-1">
                                        Arrastra tu archivo aquí o haz clic para seleccionar
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Formatos aceptados: CSV, Excel (.xlsx, .xls)
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    {/* Preview */}
                    {preview.length > 0 && !result && (
                        <div className="mt-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Vista previa ({preview.length - 1} filas)</h3>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {preview[0]?.map((header, i) => (
                                                <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {preview.slice(1).map((row, i) => (
                                            <tr key={i}>
                                                {row.map((cell, j) => (
                                                    <td key={j} className="px-4 py-2 text-sm text-gray-700">
                                                        {cell || <span className="text-gray-400">-</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Result */}
                    {result && (
                        <div className="mt-6">
                            <div className={`p-6 rounded-xl text-center ${result.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                                <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${result.failed > 0 ? 'text-yellow-500' : 'text-emerald-500'}`} />
                                <h3 className={`text-lg font-semibold ${result.failed > 0 ? 'text-yellow-800' : 'text-emerald-800'}`}>
                                    Importación Completada
                                </h3>
                                <p className="text-sm text-gray-600 mt-2">
                                    {result.success} registros importados correctamente
                                    {result.failed > 0 && `, ${result.failed} fallidos`}
                                </p>
                                {result.errors.length > 0 && (
                                    <div className="mt-4 text-left">
                                        {result.errors.map((error, i) => (
                                            <div key={i} className="flex items-start gap-2 text-sm text-red-600 mt-1">
                                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                                {error}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                    >
                        {result ? 'Cerrar' : 'Cancelar'}
                    </button>
                    {!result && (
                        <button
                            onClick={handleImport}
                            disabled={!file || isUploading}
                            className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg 
                                       hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUploading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Importando...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Importar {tabs.find(t => t.id === activeTab)?.label}
                                </>
                            )}
                        </button>
                    )}
                    {result && (
                        <button
                            onClick={() => { resetForm(); }}
                            className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                            Importar más datos
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportDataModal;
